import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import i18n from 'i18nconfig';
import PrivateRoute from 'PrivateRoute';

import ScrollToTop from 'components/ScrollToTop';
import Spinner from 'components/Spinner';
import TransitionManager from 'components/TransitionManager';
import { closeSnackbar } from 'store/features/app/appSlice';
import { getTeam } from 'store/features/team/teamServices';
import { useAppDispatch, useAppSelector } from 'store/reduxHooks';
import { getCountry } from 'utils/helpers';
import { useGuestUserInitialization } from 'utils/hooks';
import { SUPPORTED_LANGUAGES } from 'utils/languageConfig';
import { RouteTransitionProvider } from 'utils/RouteTransitionContext';
import { lazyWithReload } from 'utils/lazyWithReload';
import { WithTranslationSections } from './utils/useTranslationSections';

// Lazy load components
const Homepage = lazyWithReload(() => import('./pages/Homepage'));
const Authpage = lazyWithReload(() => import('./pages/Profile/Authpage'));
const ConfirmPaymentPage = lazyWithReload(() => import('./pages/Checkout/ConfirmPaymentPage'));
const InstantOrder = lazyWithReload(() => import('./pages/Order'));
const ProfilePage = lazyWithReload(() => import('./pages/Profile/Profilepage'));
const Orderspage = lazyWithReload(() => import('./pages/Teams/Orderspage'));
const OrderDetails = lazyWithReload(() => import('./components/Table/OrderDetails'));
const AddTeampage = lazyWithReload(() => import('./pages/Profile/AddTeampage'));
const Teampage = lazyWithReload(() => import('./pages/Teams/Teampage'));
const Bilingpage = lazyWithReload(() => import('./pages/Teams/Billingpage'));
const Page404 = lazyWithReload(() => import('./pages/Page404'));
const FileStatusListener = lazyWithReload(() => import('./components/PrintFileLIstener'));
const BusinessPage = lazyWithReload(() => import('./pages/BusinessPage'));
const RymanPage = lazyWithReload(() => import('./pages/RymanPage'));
const MailboxexPage = lazyWithReload(() => import('./pages/MailboxexPage'));
const StudentsPage = lazyWithReload(() => import('./pages/StudentsPage'));
const PrintSnackBar = lazyWithReload(() => import('./components/PrintSnackBar'));
const PrinttLocationsPage = lazyWithReload(() => import('./pages/PrinttLocations'));
const PrintingGuide = lazyWithReload(() => import('./pages/PrintingGuide'));
const GuidePage = lazyWithReload(() => import('./pages/PrintingGuide/GuidePage'));
const BlogPage = lazyWithReload(() => import('./pages/BlogPage/BlogPage'));
const BlogPostPage = lazyWithReload(() => import('./pages/BlogPage/BlogPostPage'));
const PrivacyPolicyPage = lazyWithReload(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazyWithReload(() => import('./pages/TermsAndConditionsPage'));
const DeleteAccountPage = lazyWithReload(() => import('./pages/DeleteAccountPage'));
const PricingPage = lazyWithReload(() => import('./pages/PricingPage'));
const FaqPage = lazyWithReload(() => import('./pages/FaqPage'));
const EnvironmentPage = lazyWithReload(() => import('./pages/EnvironmentPage'));
const SetNewPasswordPage = lazyWithReload(() => import('./pages/SetNewPasswordPage'));

// Sends one page's address to another in the same language
const LocaleRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { locale } = useParams();
  return <Navigate to={`/${locale}${to}`} replace />;
};

type RouteTypes = {
  path: string;
  element: React.ReactElement;
  isPrivate?: boolean;
};

const routes: RouteTypes[] = [
  { path: '/', element: <Homepage /> },
  { path: '/auth', element: <Authpage /> },
  // Old direct links to the forms: the sign-in page, opened on signing up or signing in
  { path: '/register', element: <Authpage choice='register' /> },
  { path: '/login', element: <Authpage /> },
  { path: '/congratulations', element: <ConfirmPaymentPage /> },
  { path: '/instantOrder', element: <InstantOrder /> },
  { path: '/profile', element: <ProfilePage />, isPrivate: true },
  { path: '/orders', element: <Orderspage />, isPrivate: true },
  { path: '/orders/:id', element: <OrderDetails />, isPrivate: true },
  { path: '/addteam', element: <AddTeampage />, isPrivate: true },
  { path: '/team', element: <Teampage />, isPrivate: true },
  { path: '/billing', element: <Bilingpage />, isPrivate: true },
  {
    path: '/business',
    element: (
      <WithTranslationSections sections={['pages']}>
        <BusinessPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/ryman',
    element: (
      <WithTranslationSections sections={['pages']}>
        <RymanPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/mailboxes',
    element: (
      <WithTranslationSections sections={['pages']}>
        <MailboxexPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/students',
    element: (
      <WithTranslationSections sections={['pages']}>
        <StudentsPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/locations',
    element: (
      <WithTranslationSections sections={['shopLocations']}>
        <PrinttLocationsPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/locations/:location',
    element: (
      <WithTranslationSections sections={['shopLocations']}>
        <PrinttLocationsPage />
      </WithTranslationSections>
    ),
  },
  { path: '/printingGuides', element: <PrintingGuide /> },
  {
    path: '/printingGuides/:guide',
    element: (
      <WithTranslationSections sections={['printingGuides']}>
        <GuidePage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/blog',
    element: (
      <WithTranslationSections sections={['blog']}>
        <BlogPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/blog/:slug',
    element: (
      <WithTranslationSections sections={['blog']}>
        <BlogPostPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <WithTranslationSections sections={['legalPages']}>
        <PrivacyPolicyPage />
      </WithTranslationSections>
    ),
  },
  // A short link people (and old app store listings) use for the privacy policy
  { path: '/privacy', element: <LocaleRedirect to='/privacy-policy' /> },
  {
    path: '/delete-account',
    element: (
      <WithTranslationSections sections={['legalPages']}>
        <DeleteAccountPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/terms-and-conditions',
    element: (
      <WithTranslationSections sections={['legalPages']}>
        <TermsAndConditionsPage />
      </WithTranslationSections>
    ),
  },
  {
    path: '/environment',
    element: (
      <WithTranslationSections sections={['environment']}>
        <EnvironmentPage />
      </WithTranslationSections>
    ),
  },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/faq', element: <FaqPage /> },
  // The link in the forgotten-password email
  { path: '/reset/:uidb64/:token', element: <SetNewPasswordPage /> },
  { path: '*', element: <Page404 /> },
];

const Router = () => {
  const { snackbar } = useAppSelector((state) => state.app);
  const { userType } = useAppSelector((state) => state.user);

  const dispatch = useAppDispatch();

  useGuestUserInitialization(userType);

  const getBrowserLanguage = useCallback(async () => {
    try {
      const country = await getCountry();
      const countryCode = country?.toLowerCase();

      // Map country codes to supported languages, fallback to 'en' for unsupported countries
      // (the site is UK / English only for now, so Bulgarian and Italian visitors get English too)
      const countryToLanguageMap: Record<string, string> = {
        uk: 'en',
        gb: 'en',
        'united kingdom': 'en',
        'great britain': 'en',
      };

      const detectedLanguage = countryToLanguageMap[countryCode] || 'en';

      // Only set language if not already set by URL or user preference
      if (!i18n.language || i18n.language === 'cimode') {
        i18n.changeLanguage(detectedLanguage);
      }
    } catch (error) {
      console.error('Error detecting country:', error);
      // Fallback to English if country detection fails
      if (!i18n.language || i18n.language === 'cimode') {
        i18n.changeLanguage('en');
      }
    }
  }, []);

  useEffect(() => {
    // Initialize browser language and finish initialization
    getBrowserLanguage();

    // Load team data for logged-in users
    if (userType !== 'guest') {
      dispatch(getTeam());
    }
  }, [dispatch, getBrowserLanguage, userType]);

  const handleClose = useCallback(() => {
    dispatch(closeSnackbar());
  }, [dispatch]);

  return (
    <BrowserRouter>
      <AppContent snackbar={snackbar} handleClose={handleClose} />
    </BrowserRouter>
  );
};

interface SnackbarProps {
  open: boolean;
  message: string;
  type: 'success' | 'warning' | 'error';
}

interface AppContentProps {
  snackbar: SnackbarProps;
  handleClose: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ snackbar, handleClose }) => {
  return (
    <>
      <HashRouterHandler />
      <ScrollToTop />
      <RouteTransitionProvider>
        <TransitionManager />
        <LocaleRouter>
          <Suspense fallback={<Spinner />}>
            <FileStatusListener />
            <PrintSnackBar
              open={snackbar.open}
              messages={[snackbar.message]}
              handleClose={handleClose}
              type={snackbar.type}
            />
            <Routes>
              <Route path='/' element={<DefaultLocaleRedirect />} />
              <Route path='/:locale/*' element={<LocalizedRoutes routes={routes} />} />
              <Route path='*' element={<DefaultLocaleRedirect />} />
            </Routes>
          </Suspense>
        </LocaleRouter>
      </RouteTransitionProvider>
    </>
  );
};

const LocaleRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple redirects
    if (redirectingRef.current) {
      return;
    }

    const pathSegments = location.pathname.split('/');
    const locale = pathSegments[1];

    // Check if the locale is supported
    const isValidLocale = locale && SUPPORTED_LANGUAGES.includes(locale as any);
    // A two-letter first segment is a language; anything longer is a page (/reset/..., /pricing) whose link has no
    // language, which keeps its whole path below
    const looksLikeLocale = /^[a-z]{2}$/i.test(locale || '');

    // If we're at the root or the first segment isn't a valid locale
    if (!isValidLocale && looksLikeLocale) {
      // Handle unsupported locale (like 'fr') - redirect to English
      redirectingRef.current = true;
      setIsRedirecting(true);

      // Remove the unsupported locale and use English
      const pathWithoutUnsupportedLocale = pathSegments.slice(2).join('/');
      const newPath = `/en${pathWithoutUnsupportedLocale ? '/' + pathWithoutUnsupportedLocale : ''}`;
      const fullPath = `${newPath}${location.search}${location.hash}`;

      // Change i18n language to English
      i18n.changeLanguage('en');

      setTimeout(() => {
        navigate(fullPath, { replace: true });
        redirectingRef.current = false;
      }, 0);
    } else if (!isValidLocale) {
      // Handle case where there's no locale at all
      redirectingRef.current = true;
      setIsRedirecting(true);

      // Use current i18n language or fallback to 'en'
      const defaultLocale = i18n.language && SUPPORTED_LANGUAGES.includes(i18n.language as any) ? i18n.language : 'en';

      // Preserve the full path
      let pathWithoutLocale = location.pathname;
      if (pathWithoutLocale === '/') {
        pathWithoutLocale = '';
      }

      const newPath = `/${defaultLocale}${pathWithoutLocale}`;
      const fullPath = `${newPath}${location.search}${location.hash}`;

      setTimeout(() => {
        navigate(fullPath, { replace: true });
        redirectingRef.current = false;
      }, 0);
    } else {
      // Valid locale - only change language if it's different from current
      if (i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
      setIsRedirecting(false);
      redirectingRef.current = false;
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return <>{children}</>;
};

const LocalizedRoutes: React.FC<{ routes: RouteTypes[] }> = ({ routes }) => {
  return (
    <Routes>
      {routes.map(({ path, element, isPrivate }) => (
        <Route
          key={path}
          path={path === '/' ? '/' : `${path}`}
          element={isPrivate ? <PrivateRoute element={element} isPrivate={isPrivate} /> : element}
        />
      ))}
      <Route path='*' element={<Page404 />} />
    </Routes>
  );
};

const DefaultLocaleRedirect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract locale from URL if present
    const pathSegments = location.pathname.split('/');
    const urlLocale = pathSegments[1];

    // Check if URL contains a valid locale
    const isValidLocale = urlLocale && SUPPORTED_LANGUAGES.includes(urlLocale as any);

    let targetLocale = 'en'; // Default fallback

    if (isValidLocale) {
      // Use locale from URL and set it immediately
      targetLocale = urlLocale;
      if (i18n.language !== urlLocale) {
        i18n.changeLanguage(urlLocale);
      }
    } else {
      // Use current i18n language if it's supported, otherwise fallback to 'en'
      targetLocale = i18n.language && SUPPORTED_LANGUAGES.includes(i18n.language as any) ? i18n.language : 'en';
    }

    // Preserve the full path including blog pages
    let pathWithoutLocale = location.pathname;
    if (pathWithoutLocale === '/') {
      pathWithoutLocale = '';
    }

    const newPath = `/${targetLocale}${pathWithoutLocale}`;
    const fullPath = `${newPath}${location.search}${location.hash}`;

    navigate(fullPath, { replace: true });
  }, [navigate, location]);

  return <Spinner hasText={true} />;
};

const HashRouterHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash.startsWith('#!/')) {
      const newPath = location.hash.replace('#!/', '');
      navigate(newPath, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

export default Router;
